import { Alert, Button, Empty, Input, Select, Skeleton, Table, Tag, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { apiService } from '../../services/api';
import { formatApiErrorDetail } from '../../utils/formatApiErrorDetail';
import { buildExportRows, defaultWindow, formatBytes } from './exportsTable';

// SM3. «Журналы S3 → Выгрузки»: сервер × сутки — строки, размер, когда загружено, пропуски и «скачать»
// (подписанные ссылки на файлы суток, живут 15 минут). Сутки — UTC, как их режет Midori.

const STATE_TAG = {
  ok: { color: 'green', text: 'в бакете' },
  missing: { color: 'red', text: 'пропали из бакета' },
  expired: { color: 'default', text: 'истекли (184 дня)' },
  invalid: { color: 'orange', text: 'не по формату' },
  gap: { color: 'red', text: 'нет выгрузки' },
};

const pick = (response) => response?.data?.data ?? null;

const JournalS3Exports = () => {
  const initial = useMemo(() => defaultWindow(new Date()), []);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [installation, setInstallation] = useState(undefined);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [indexing, setIndexing] = useState(false);
  const [error, setError] = useState(null);
  const [links, setLinks] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(pick(await apiService.getJournalS3Days({
        date_from: dateFrom, date_to: dateTo, installation_uuid: installation || undefined,
      })));
    } catch (e) {
      setError(formatApiErrorDetail(e) || 'Не удалось получить выгрузки');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, installation]);

  useEffect(() => { load(); }, [load]);

  const indexNow = async () => {
    setIndexing(true);
    try {
      const stats = pick(await apiService.indexJournalS3Now());
      message.success(`Бакет обойдён: новых ревизий ${stats?.new ?? 0}, пропало ${stats?.missing ?? 0}`);
      await load();
    } catch (e) {
      message.error(formatApiErrorDetail(e) || 'Обход бакета не удался');
    } finally {
      setIndexing(false);
    }
  };

  const fetchLinks = async (dayId) => {
    try {
      const got = pick(await apiService.getJournalS3DayLinks(dayId));
      setLinks((prev) => ({ ...prev, [dayId]: got?.files || [] }));
    } catch (e) {
      message.error(formatApiErrorDetail(e) || 'Не удалось получить ссылки');
    }
  };

  const rows = useMemo(() => buildExportRows(data), [data]);
  const installationOptions = (data?.installations || []).map((item) => ({
    value: item.installation_uuid,
    label: `${item.connection_name || 'без подключения'} — ${item.installation_name} / ${item.installation_uuid.slice(0, 8)}`,
  }));
  const lagging = (data?.installations || []).filter((item) => item.lagging);

  const columns = [
    { title: 'Сутки (UTC)', dataIndex: 'day', key: 'day', width: 120 },
    {
      title: 'Сервер',
      key: 'server',
      render: (_, row) => (
        <div>
          <div>{row.connection_name || <span className="sett-cell--muted">без подключения</span>}</div>
          <div className="sett-cell--muted">{`${row.installation_name} / ${row.installation_uuid.slice(0, 8)}`}</div>
        </div>
      ),
    },
    {
      title: 'Ревизия',
      key: 'revision',
      width: 110,
      render: (_, row) => (row.revision ? `r${row.revision}${row.source === 'default' ? ' (опоздавшие)' : ''}` : '—'),
    },
    { title: 'Строк', dataIndex: 'rows', key: 'rows', width: 90, render: (value) => (value ?? '—') },
    { title: 'Размер', dataIndex: 'bytes', key: 'bytes', width: 100, render: (value) => formatBytes(value) },
    {
      title: 'Загружено',
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
      width: 170,
      render: (value) => (value ? new Date(value).toLocaleString() : '—'),
    },
    {
      title: 'Состояние',
      key: 'state',
      width: 170,
      render: (_, row) => {
        const tag = STATE_TAG[row.state] || { color: 'default', text: row.state };
        return <Tag color={tag.color} title={row.error || undefined}>{tag.text}</Tag>;
      },
    },
    {
      title: '',
      key: 'download',
      width: 260,
      render: (_, row) => {
        if (!row.id || row.state !== 'ok') return null;
        const files = links[row.id];
        if (!files) {
          return <Button size="small" onClick={() => fetchLinks(row.id)} aria-label={`Скачать ${row.day}`}>Скачать</Button>;
        }
        return (
          <div>
            {files.map((file) => (
              <div key={file.name}>
                <a href={file.url} target="_blank" rel="noopener noreferrer">{file.name}</a>
                {file.bytes ? <span className="sett-cell--muted">{` · ${formatBytes(file.bytes)}`}</span> : null}
              </div>
            ))}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Заголовок и вкладки — JournalS3Layout (SM4: «Выгрузки» | «Поиск по IP»). */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Сутки журнала доступа серверов Midori в общем бакете"
        description={
          'Каталог обновляет служба journal-s3-index раз в 30 минут; «Обойти сейчас» — то же сразу. Сутки D выгружаются '
          + 'после 00:15 UTC D+1. Хранятся 184 дня. «Скачать» даёт ссылки на файлы суток на 15 минут.'
        }
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ width: 160 }}
          aria-label="С даты" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ width: 160 }}
          aria-label="По дату" />
        <Select allowClear placeholder="Все серверы" style={{ minWidth: 280 }} options={installationOptions}
          value={installation} onChange={setInstallation} aria-label="Сервер" />
        <Button onClick={load} loading={loading}>Показать</Button>
        <Button onClick={indexNow} loading={indexing}>Обойти сейчас</Button>
      </div>
      {error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} />}
      {lagging.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Выгрузка отстаёт: ${lagging.map((item) => `${item.connection_name || item.installation_name} (последние сутки ${item.last_day})`).join(', ')}`}
        />
      )}
      {loading && !data ? (
        <Skeleton active />
      ) : (
        <Table
          rowKey="key"
          size="small"
          pagination={{ pageSize: 50, hideOnSinglePage: true }}
          dataSource={rows}
          columns={columns}
          locale={{ emptyText: <Empty description="За эти сутки выгрузок нет" /> }}
        />
      )}
    </div>
  );
};

export default JournalS3Exports;
